import React, { useMemo, useState, useId, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { confirmPasswordReset } from 'firebase/auth'
import { firebaseAuth } from '../firebase/firebaseClient'
import Loader from '../components/Loader'
import Button from '../components/UI/button'
import Input from '../components/UI/input'
import { useTranslation } from 'react-i18next'

const ResetPassword = () => {
	const uidHook = useId()
	const newId = `rp-new-${uidHook}`
	const confirmId = `rp-confirm-${uidHook}`
	const msgId = `rp-msg-${uidHook}`
	const mismatchId = `rp-mismatch-${uidHook}`

	const { t, i18n } = useTranslation()
	const isFr = i18n.resolvedLanguage === 'fr'

	const toggleLanguage = () => {
		const nextLang = isFr ? 'en' : 'fr'
		i18n.changeLanguage(nextLang)
		localStorage.setItem('lang', nextLang)
	}

	useEffect(() => {
		const savedLang = localStorage.getItem('lang')
		if (savedLang) i18n.changeLanguage(savedLang)
	}, [])

	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [message, setMessage] = useState('')
	const [messageType, setMessageType] = useState('')
	const [loading, setLoading] = useState(false)

	const location = useLocation()
	const oobCode = useMemo(() => {
		const query = new URLSearchParams(location.search)
		return query.get('oobCode')
	}, [location.search])

	const passwordsMismatch =
		newPassword.length > 0 && confirmPassword.length > 0 && newPassword !== confirmPassword

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setMessage('')
		setMessageType('')

		if (!oobCode) {
			setMessage(t('resetPassword.invalidLink'))
			setMessageType('error')
			setLoading(false)
			return
		}
		if (newPassword !== confirmPassword) {
			setMessage(t('resetPassword.mismatch'))
			setMessageType('error')
			setLoading(false)
			return
		}

		try {
			await confirmPasswordReset(firebaseAuth, oobCode, newPassword)
			setMessage(t('resetPassword.success'))
			setMessageType('success')
			setNewPassword('')
			setConfirmPassword('')
		} catch (error) {
			setMessage(t('resetPassword.error'))
			setMessageType('error')
		}
		setLoading(false)
	}

	return (
		<>
			{loading ? (
				<Loader />
			) : (
				<div
					className="custom-container"
					style={{ position: 'absolute', width: '100%', left: '0px' }}
				>
					<button
						type="button"
						className={`lang-switch ${isFr ? 'fr' : 'en'}`}
						onClick={toggleLanguage}
					>
						<span className="lang-switch-circle">{isFr ? 'FR' : 'EN'}</span>
					</button>

					<form className="left" onSubmit={handleSubmit} noValidate aria-describedby={message ? msgId : undefined}>
						<h1>{t('resetPassword.header')}</h1>

						<p><strong>{t('resetPassword.subtext')}</strong></p>

						{message && (
							<div
								id={msgId}
								className={messageType === 'error' ? 'error' : 'success'}
								role={messageType === 'error' ? 'alert' : 'status'}
								aria-live={messageType === 'error' ? 'assertive' : 'polite'}
								aria-atomic="true"
								tabIndex={-1}
							>
								{message}
							</div>
						)}

						<div className="password-field">
							<Input
								id={newId}
								name="newPassword"
								type="password"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								placeholder={t('resetPassword.newPassword')}
								required
								minLength={8}
								autoComplete="new-password"
								showPasswordToggle
								aria-invalid={passwordsMismatch ? 'true' : 'false'}
							/>
						</div>

						<div className="password-field">
							<Input
								id={confirmId}
								name="confirmPassword"
								type="password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								placeholder={t('resetPassword.confirmPassword')}
								required
								minLength={8}
								autoComplete="new-password"
								showPasswordToggle
								aria-invalid={passwordsMismatch ? 'true' : 'false'}
								aria-describedby={passwordsMismatch ? mismatchId : undefined}
							/>
							<p
								id={mismatchId}
								className="visually-hidden"
								role="status"
								aria-live="polite"
								aria-atomic="true"
							>
								{passwordsMismatch ? t('resetPassword.mismatch') : ''}
							</p>
						</div>

						<div className="forgot-password-link" />

						<div className="submit-btn-wrapper">
							<Button variant="secondary" type="submit" size="lg">
								{t('resetPassword.submit')}
							</Button>
						</div>

						<Link to="/" className="other-page-link">
							{t('resetPassword.backToLogin')}
						</Link>
					</form>

					<div className="right">
						<h1>{t('resetPassword.rightTitle')}</h1>
						<p>{t('resetPassword.rightText')}</p>
						<div className="submit-btn-wrapper">
							<Link to="/">
								<Button variant="secondary" type="button" size="lg">
									{t('login.header')}
								</Button>
							</Link>
						</div>
					</div>
				</div>
			)}
		</>
	)
}

export default ResetPassword